import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import ProductCatalog from './components/ProductCatalog';
import VoiceController from './components/VoiceController';
import OrderSummary from './components/OrderSummary';
import API_BASE_URL from './config/api';

function App() {
  const [view, setView] = useState('home'); // 'home' or 'voice-call'
  const [sessionId, setSessionId] = useState(null);
  const [cartItems, setCartItems] = useState([]);
  const [orderState, setOrderState] = useState({ items: [], total: 0 });
  const [showCart, setShowCart] = useState(false);
  const [orderConfirmation, setOrderConfirmation] = useState(null);

  // Always keep a current ref to orderState to avoid stale closure when auto-ending call
  const orderStateRef = useRef(orderState);
  useEffect(() => {
    orderStateRef.current = orderState;
  }, [orderState]);

  // Emoji fallback for cart items without images
  const getCartItemEmoji = (item) => {
    const name = (item.name || '').toLowerCase();
    if (name.includes('juice') || name.includes('orange')) return '🍊';
    if (name.includes('lemon') || name.includes('mint')) return '🍋';
    if (name.includes('ayran') || name.includes('yogurt')) return '🥛';
    if (name.includes('water')) return '💧';
    if (name.includes('cola') || name.includes('pepsi')) return '🥤';
    if (name.includes('baklava')) return '🥮';
    if (name.includes('kunafa')) return '🍰';
    if (name.includes('umm ali')) return '🍮';
    if (name.includes('hummus')) return '🫘';
    if (name.includes('falafel')) return '🧆';
    if (name.includes('baba')) return '🍆';
    if (name.includes('salad')) return '🥗';
    if (name.includes('garlic') || name.includes('sauce')) return '🧄';
    if (name.includes('chicken')) return name.includes('plate') ? '🍗' : '🌯';
    if (name.includes('beef')) return name.includes('plate') ? '🥩' : '🌯';
    if (name.includes('mixed') || name.includes('family')) return '🍽️';
    return '🌯';
  };

  useEffect(() => {
    // Initialize session on mount
    console.log('Initializing session...');
    fetch(`${API_BASE_URL}/api/session/init`, { method: 'POST' })
      .then(res => {
        console.log('Session init response status:', res.status);
        return res.json();
      })
      .then(data => {
        console.log('Session initialized:', data.sessionId);
        setSessionId(data.sessionId);
      })
      .catch(err => {
        console.error('Failed to initialize session:', err);
        alert('Failed to connect to server. Please make sure the backend is running.');
      });
  }, []);

  const handleAddToCart = (product) => {
    setCartItems(prevItems => {
      const existingItem = prevItems.find(item => item.id === product.id);
      if (existingItem) {
        return prevItems.map(item =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      } else {
        return [...prevItems, { ...product, quantity: 1, customizations: [] }];
      }
    });
  };

  const handleRemoveFromCart = (productId) => {
    setCartItems(prevItems => prevItems.filter(item => item.id !== productId));
  };

  const handleUpdateQuantity = (productId, newQuantity) => {
    if (newQuantity === 0) {
      handleRemoveFromCart(productId);
    } else {
      setCartItems(prevItems =>
        prevItems.map(item =>
          item.id === productId ? { ...item, quantity: newQuantity } : item
        )
      );
    }
  };

  const calculateTotal = () => {
    return cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
  };

  const handleStartVoiceCall = () => {
    // Generate a unique session ID for this call
    const newSessionId = `aoc-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`;
    setSessionId(newSessionId);
    setOrderState({ items: [], total: 0 });
    setView('voice-call');
  };

  const handleEndCall = () => {
    // Use ref to get the LATEST orderState — avoids stale closure when call auto-ends via voice
    const currentOrderState = orderStateRef.current;
    console.log('📞 Call ended. Order state:', currentOrderState);

    // Show order confirmation if there are items
    if (currentOrderState.items && currentOrderState.items.length > 0) {
      const orderNumber = `HL-${Date.now().toString(36).toUpperCase()}`;
      const confirmation = {
        orderNumber,
        items: [...currentOrderState.items],
        total: currentOrderState.total,
        date: new Date(),
        estimatedTime: Math.floor(Math.random() * 10) + 15, // 15-25 min
      };

      setOrderConfirmation(confirmation);
      setOrderState({ items: [], total: 0 }); // Reset order state
    }

    setView('home');
  };

  const handleCheckout = () => {
    if (cartItems.length === 0) return;

    const orderNumber = `HL-${Date.now().toString(36).toUpperCase()}`;
    const confirmation = {
      orderNumber,
      items: [...cartItems],
      total: calculateTotal(),
      date: new Date(),
      estimatedTime: Math.floor(Math.random() * 10) + 15, // 15-25 min
    };

    setOrderConfirmation(confirmation);
    setCartItems([]);
    setShowCart(false);
  };

  const closeConfirmation = () => {
    setOrderConfirmation(null);
  };

  const cartItemCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  // Voice Call View
  if (view === 'voice-call') {
    if (!sessionId) {
      return (
        <div style={{ padding: '50px', textAlign: 'center' }}>
          <h2>Initializing voice call...</h2>
          <p>Please wait while we connect you to our AI assistant.</p>
        </div>
      );
    }

    return (
      <VoiceController
        sessionId={sessionId}
        orderState={orderState}
        setOrderState={setOrderState}
        onEndCall={handleEndCall}
      />
    );
  }

  // Home View (E-commerce)
  return (
    <div className="App">
      {/* Header */}
      <header className="App-header-new">
        <div className="brand-logo">
          <span className="logo-icon">🌯</span>
          <h1>AI Order Call</h1>
        </div>

        <div className="header-actions">
          <button className="cart-button" onClick={() => setShowCart(!showCart)}>
            🛒 Cart ({cartItemCount})
            {cartItemCount > 0 && <span className="cart-badge">{cartItemCount}</span>}
          </button>
        </div>
      </header>

      {/* Hero Banner */}
      <section className="hero-banner">
        <div className="hero-content">
          <div className="hero-text">
            <h2 className="hero-title">
              <span className="hero-title-line1">AI Order</span>
              <span className="hero-title-line2">Call</span>
            </h2>
            <p className="hero-tagline">Authentic Middle Eastern flavors crafted with love. Fresh, bold, and unforgettable — one bite and you'll be hooked!</p>
            <div className="hero-buttons">
              <button className="hero-cta" onClick={() => document.querySelector('.App-main-new')?.scrollIntoView({ behavior: 'smooth' })}>
                Order Now
              </button>
              <button className="hero-cta-secondary" onClick={handleStartVoiceCall}>
                📞 Voice Order
              </button>
            </div>
          </div>
          <div className="hero-image">
            <img src="/images/menu/chicken-shawarma-plate.jpeg" alt="Shawarma Plate" />
          </div>
        </div>
        <div className="hero-dots">
          <span className="dot active"></span>
          <span className="dot"></span>
        </div>
      </section>

      {/* Main Content */}
      <main className="App-main-new">
        <ProductCatalog
          onAddToCart={handleAddToCart}
          onUpdateQuantity={handleUpdateQuantity}
          onRemoveFromCart={handleRemoveFromCart}
          cartItems={cartItems}
        />
      </main>

      {/* Cart Sidebar */}
      {showCart && (
        <div className="cart-overlay" onClick={() => setShowCart(false)}>
          <div className="cart-sidebar" onClick={(e) => e.stopPropagation()}>
            <div className="cart-header">
              <h2>Your Cart</h2>
              <button className="close-cart" onClick={() => setShowCart(false)}>×</button>
            </div>

            <div className="cart-items">
              {cartItems.length === 0 ? (
                <div className="empty-cart">
                  <p>Your cart is empty</p>
                  <p className="empty-cart-hint">Add items from the menu!</p>
                </div>
              ) : (
                <>
                  {cartItems.map(item => (
                    <div key={item.id} className="cart-item">
                      <div className="cart-item-image">
                        {item.image_url ? (
                          <img src={item.image_url} alt={item.name} />
                        ) : (
                          <span className="cart-item-emoji">{getCartItemEmoji(item)}</span>
                        )}
                      </div>
                      <div className="cart-item-info">
                        <h4>{item.name}</h4>
                        <p className="cart-item-price">AED {(item.price * item.quantity).toFixed(2)}</p>
                      </div>
                      <div className="cart-item-controls">
                        <button onClick={() => handleUpdateQuantity(item.id, item.quantity - 1)}>−</button>
                        <span>{item.quantity}</span>
                        <button onClick={() => handleUpdateQuantity(item.id, item.quantity + 1)}>+</button>
                      </div>
                      <button
                        className="remove-item"
                        onClick={() => handleRemoveFromCart(item.id)}
                      >
                        🗑️
                      </button>
                    </div>
                  ))}

                  <div className="cart-total">
                    <h3>Total</h3>
                    <h3>AED {calculateTotal().toFixed(2)}</h3>
                  </div>

                  <button className="checkout-button" onClick={handleCheckout}>
                    Checkout
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Order Confirmation Modal */}
      {orderConfirmation && (
        <div className="confirmation-overlay" onClick={closeConfirmation}>
          <div className="confirmation-modal" onClick={(e) => e.stopPropagation()}>
            <div className="confirmation-icon">
              <div className="checkmark-circle">
                <svg viewBox="0 0 52 52">
                  <circle cx="26" cy="26" r="25" fill="none" />
                  <path fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8" />
                </svg>
              </div>
            </div>

            <h2 className="confirmation-title">Order Confirmed!</h2>
            <p className="confirmation-order-num">Order #{orderConfirmation.orderNumber}</p>

            <div className="confirmation-items">
              {orderConfirmation.items.map(item => (
                <div key={item.id} className="confirmation-item">
                  <div className="confirmation-item-image">
                    {item.image_url ? (
                      <img src={item.image_url} alt={item.name} />
                    ) : (
                      <span>{getCartItemEmoji(item)}</span>
                    )}
                  </div>
                  <div className="confirmation-item-details">
                    <span className="confirmation-item-name">{item.name}</span>
                    <span className="confirmation-item-qty">×{item.quantity}</span>
                  </div>
                  <span className="confirmation-item-price">AED {(item.price * item.quantity).toFixed(2)}</span>
                </div>
              ))}
            </div>

            <div className="confirmation-total">
              <span>Total</span>
              <span>AED {orderConfirmation.total.toFixed(2)}</span>
            </div>

            <div className="confirmation-eta">
              <span className="eta-icon">⏱️</span>
              <span>Estimated ready in <strong>{orderConfirmation.estimatedTime} mins</strong></span>
            </div>

            <button className="confirmation-done-btn" onClick={closeConfirmation}>
              Done
            </button>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="App-footer">
        <p>Powered by AI Voice Technology • Fast & Delicious</p>
      </footer>
    </div>
  );
}

export default App;
